import React, { useEffect, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/UI/Button';
import { useApp, useAppDispatchers } from '../../state/app';
import { useUser, useUserDispatchers } from '../../state/user';
import { useToggles } from '../../state/toggles';
import { AnalyticsContext } from '../../ModularReduxAnalytics';

import Loader from '../../components/UI/Loader';

import css from './VerifyDeclined.module.scss';

export const continueUrls = {
  risk: 'greenlight.com/risk',
  compliance_risk: 'greenlight.com/compliance-risk',
  compliance: 'greenlight.com/compliance'
};

const VerifyDeclined = () => {
  const [continueUrl, setContinueUrl] = useState(
    'https://greenlight.com/verify'
  );
  const { loading } = useApp();
  const { registeredUser, failureReason, hasExceededMaxVerificationAttempts } =
    useUser();
  const { getFailureReason, setIsKycRetryAttempt, clearError } =
    useAppDispatchers();
  const { setNextRegStep } = useUserDispatchers();
  const { kycRetryEnabled } = useToggles();
  const { analytics } = useContext(AnalyticsContext);
  const history = useHistory();

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.page('Registration', 'Identity Verification Failed');
    getFailureReason({
      userId: registeredUser?.user?.id
    });
  }, []);

  useEffect(() => {
    if (failureReason === 'hard_decline') {
      analytics.trackEvent('KYC Hard Declined');
      history.replace('/verify-hard-declined');
    } else if (failureReason === 'compliance_risk') {
      analytics.trackEvent('KYC Soft Declined');
    } else {
      // Since we're keeping one queue for the short term we can just send them to /verify, but leaving this here in case that changes
      // setContinueUrl(continueUrls[failureReason]);
      analytics.trackEvent('KYC Declined Viewed');
    }
  }, [failureReason]);

  const onKycRetry = () => {
    setIsKycRetryAttempt({ isKycRetryAttempt: true });
    setNextRegStep('register');
  };

  const onKycUpload = () => {
    analytics.trackEvent('KYC Submit Docs Click');
    window.location.href = continueUrl;
  };

  const shouldShowKycRetryButton =
    !hasExceededMaxVerificationAttempts && kycRetryEnabled.enabled;

  if (loading || !failureReason) {
    return <Loader />;
  }

  return (
    <Layout>
      <>
        <div className={css.verifyDeclined}>
          <h1 data-e2e="verify-declined-header">We need a little more info</h1>
          <h2 data-e2e="message" data-testid="verify-text">
            Welcome to Greenlight! To continue your registration process, please
            follow the steps below to verify your identity. Then continue to our
            secure document portal.
          </h2>
          <ol>
            <li>
              A picture of <strong>your state-issued ID</strong> with your
              registered address, <strong>and</strong>
            </li>
            <li>
              A picture, or PDF version, of a{' '}
              <strong>
                recent utility bill, credit card or mortgage statement
              </strong>{' '}
              that shows your name and address
            </li>
          </ol>
          <p>Please allow several days for us to verify your documents.</p>
          <div>
            <Button onClick={onKycUpload} text="Continue to upload documents" />
          </div>

          {shouldShowKycRetryButton && (
            <Button
              outline={true}
              onClick={onKycRetry}
              text="Resubmit my information"
              data-testid="kyc-retry-button"
            />
          )}
        </div>
      </>
    </Layout>
  );
};

export default VerifyDeclined;
